import { useCallback, useEffect, useMemo, useState } from "react";
import { createHash } from "crypto";
import {
  useQuranOptions,
  useQuranStateUpdate,
} from "@/app/providers/QuranContextProvider";
import { useTheme } from "@/app/providers/QuranThemeProvider";

const fontCache = new Map<string, Promise<FontFace>>();
const fontLoadStatus = new Map<string, boolean>();

const useFonts = (fontName: string) => {
  const [isFontLoaded, setIsFontLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { quranFont } = useQuranOptions();
  const { setQuranFont } = useQuranStateUpdate();

  const { resolvedColorMode } = useTheme();

  const fontHash = useMemo(
    () => createHash("md5").update(fontName).digest("hex").toString(),
    [fontName],
  );

  const updateTajweedFont = useCallback(() => {
    if (
      quranFont &&
      quranFont.includes("tajweed") &&
      quranFont !== `tajweed-${resolvedColorMode}`
    ) {
      setQuranFont(`tajweed-${resolvedColorMode}`);
    }
  }, [quranFont, resolvedColorMode, setQuranFont]);

  useEffect(() => {
    updateTajweedFont();
  }, [updateTajweedFont]);

  const loadFont = useCallback(async () => {
    const fonts = ["hafs1", "hafs2", "tajweed-dark", "tajweed-light"];
    if (quranFont && !fonts.includes(quranFont)) {
      setIsFontLoaded(false);
      setIsLoading(false);
      return;
    }

    if (fontLoadStatus.get(fontHash)) {
      setIsFontLoaded(true);
      setIsLoading(false);
      return;
    }

    setIsFontLoaded(false);
    setIsLoading(true);
    let fontUrlPart = quranFont;
    if (quranFont && quranFont.includes("tajweed")) {
      fontUrlPart = `hafs-${quranFont.split("-")[0]}/${resolvedColorMode}`;
    }

    const fontUrl = `/fonts/${fontUrlPart}/${fontHash}.woff2`;

    if (fontCache.has(fontHash)) {
      const cachedPromise = fontCache.get(fontHash);
      if (cachedPromise) {
        await cachedPromise;
        setIsFontLoaded(true);
        return;
      }
    }

    try {
      const fontFace = new FontFace(fontName, `url(${fontUrl})`);
      const loadPromise = fontFace.load();

      fontCache.set(fontHash, loadPromise);

      await loadPromise;
      document.fonts.add(fontFace);
      fontLoadStatus.set(fontHash, true);
      setIsFontLoaded(true);
      setIsLoading(false);
    } catch (error) {
      console.error(`Error loading font ${fontName}:`, error);
      setIsFontLoaded(false);
      setIsLoading(false);
    }
  }, [fontName, quranFont, resolvedColorMode, fontHash]);

  useEffect(() => {
    loadFont();
  }, [loadFont]);

  return { isFontLoaded, isLoading };
};

export default useFonts;
